import { Plus, Server } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import {
  Badge,
  Button,
  Card,
  CardSubtitle,
  CardTitle,
  FormField,
  Input,
  Modal,
  Textarea,
} from '@/components/ui';
import { useNotify } from '@/hooks/useNotify';
import { useT } from '@/i18n';
import { api, type AdminServer } from '@/lib/api';
import { formatNumber } from '@/lib/format';
import { AdminSkeleton, Detail, useAdminData } from './AdminLayout';

export function AdminServersPage() {
  const t = useT();
  const { servers, reload } = useAdminData();
  const [ipTarget, setIpTarget] = useState<AdminServer | null>(null);

  return (
    <AdminSkeleton>
      <div className="space-y-4">
        {servers.map((server) => (
          <Card key={server.id} padding="none">
            <div className="flex items-start justify-between gap-3 border-b border-line px-5 py-4">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-[11px] bg-acc-soft text-acc">
                  <Server size={17} />
                </span>
                <div>
                  <CardTitle>{server.name}</CardTitle>
                  <CardSubtitle>
                    {server.hostname} &middot; {server.location ?? 'ohne Standortangabe'}
                  </CardSubtitle>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge status={server.status.toLowerCase()}>{server.status}</Badge>
                <Button variant="secondary" size="sm" icon={<Plus size={14} />} onClick={() => setIpTarget(server)}>
                  {t('admin.ipamReserve')}
                </Button>
              </div>
            </div>

            <div className="grid gap-4 p-5 sm:grid-cols-2 lg:grid-cols-4">
              <Detail label={t('admin.publicIp')} value={server.publicIp} mono />
              <Detail label={t('admin.wanInterface')} value={server.wanInterface} mono />
              <Detail label={t('admin.internalNet')} value={server.ipv4PoolCidr} mono />
              <Detail
                label={t('admin.ipv6Pools')}
                value={server.ipv6Pools.map((pool) => pool.cidr).join(', ') || t('common.notConfigured')}
                mono
              />
              <Detail
                label={t('admin.portRange')}
                value={`${server.listenPortRange[0]} - ${server.listenPortRange[1]}`}
                mono
              />
              <Detail
                label={t('admin.ifIndexRange')}
                value={`wg-s/wg-d ${server.ifIndexRange[0]} - ${server.ifIndexRange[1]}`}
                mono
              />
              <Detail label={t('nav.instances')} value={formatNumber(server.instanceCount)} />
              <Detail label={t('admin.capacity')} value={`${formatNumber(server.capacityMbps)} Mbit/s`} />
            </div>

            <div className="border-t border-line px-5 py-4">
              <p className="mb-2 text-[10.5px] font-bold tracking-wider text-t2 uppercase">
                {t('admin.ipv4Pools')}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {server.publicIpPools.length === 0 && (
                  <p className="text-[13px] text-t2">{t('admin.ipamNoPools')}</p>
                )}
                {server.publicIpPools.map((pool) => (
                  <Badge key={pool.id} variant="accent" className="font-mono">
                    {pool.cidr} · {pool.addressCount}
                    {pool.gateway ? ` · GW ${pool.gateway}` : ''}
                  </Badge>
                ))}
              </div>
            </div>
          </Card>
        ))}

        {servers.length === 0 && (
          <Card>
            <p className="py-8 text-center text-[13px] text-t2">{t('admin.noServer')}</p>
          </Card>
        )}
      </div>

      <AddHostIpDialog server={ipTarget} onClose={() => setIpTarget(null)} onDone={reload} />
    </AdminSkeleton>
  );
}

function AddHostIpDialog({
  server,
  onClose,
  onDone,
}: {
  server: AdminServer | null;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const notify = useNotify();
  const [text, setText] = useState('');
  const [gateway, setGateway] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (server) {
      setText('');
      setGateway(server.publicIpPools[0]?.gateway ?? '');
      setError(null);
    }
  }, [server]);

  if (!server) return null;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);

    const addresses = text
      .split(/[\s,;]+/)
      .map((entry) => entry.trim())
      .filter(Boolean);

    try {
      // Ein CIDR legt einen Pool an, einzelne Adressen ergaenzen einen vorhandenen
      const cidrs = addresses.filter((entry) => entry.includes('/'));
      const singles = addresses.filter((entry) => !entry.includes('/'));

      let added = 0;
      for (const cidr of cidrs) {
        const result = await api.admin.createIpv4Pool(server.id, {
          cidr,
          gateway: gateway.trim() || undefined,
          strategy: 'DESCENDING',
        });
        added += result.added;
      }

      if (singles.length > 0) {
        const pool = server.publicIpPools[0];
        if (!pool) throw new Error('Bitte zuerst ein Subnetz anlegen.');
        const result = await api.admin.addAddresses(pool.id, singles);
        added += result.added;
      }

      notify.success(`${added} ${added === 1 ? 'Adresse' : 'Adressen'} aufgenommen.`);
      onClose();
      await onDone();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Adressen konnten nicht aufgenommen werden');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={`Öffentliche IPv4 für ${server.name}`}>
      <form onSubmit={submit} className="space-y-4">
        <p className="-mt-1 text-[12.5px] text-t2">
          Ein Subnetz in CIDR-Schreibweise legt einen Pool an; die Vergabe erfolgt anschließend
          absteigend ab der höchsten Adresse. Einzelne Adressen ergänzen den bestehenden Pool.
        </p>
        <FormField label="Subnetz oder Adressen" required hint="Eine pro Zeile oder kommagetrennt.">
          <Textarea
            className="h-28 font-mono text-xs"
            required
            placeholder={'95.215.32.192/26'}
            value={text}
            onChange={(event) => setText(event.target.value)}
          />
        </FormField>
        <FormField label="Gateway" hint="Wird nicht in den Pool aufgenommen.">
          <Input
            className="font-mono text-xs"
            placeholder="95.215.32.1"
            value={gateway}
            onChange={(event) => setGateway(event.target.value)}
          />
        </FormField>
        {error && (
          <div className="rounded-[var(--radius-vela-md)] border border-line bg-bad-soft px-3 py-2 text-[13px] text-bad">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button type="submit" disabled={busy}>
            Aufnehmen
          </Button>
        </div>
      </form>
    </Modal>
  );
}
